import { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';

import useAuth from '../../auth/useAuth';
import { useSoundStatus } from '../../context/SoundStatusContext';

import Layout from '../UI/introGameLayout/layout';

import BackgroundImage from './images/game-background-blurred.jpg';

import {
  coursesQuery,
  gamesQuery,
  scenarioLevelsQuery
} from './api';

import {
  tutorialQuestions,
  tutorialCorrectDecisions,
  topOptions,
  changeTypeOptions,
  coreTypeOptions,
  transactionTypeOptions
} from './data';

import Modal from '../UI/modal';
import BalanceSheet from '../UI/balancedEquation/balanceSheet';

import TransactionRecords from '../UI/balancedEquation/transactionRecords';

import './styles.css';

import GameProcessSound from './sounds/game-process.mp3';
import ClickSound from './sounds/click.mp3';
import UpdateBalanceSheetSound from './sounds/update-balance-sheet.mp3';
import AnswerCorrectSound from './sounds/answer-correct.mp3';
import AnswerWrongSound from './sounds/answer-wrong.mp3';
import ItemDroppedSound from './sounds/item-dropped.mp3';

import SignMinus from './images/icon-minus.svg';
import SignPlus from './images/icon-plus.svg';

export default function BalancedEquationTutorial() {
  const gameProcessEffect = useRef(new Audio(GameProcessSound));
  
  const clickEffect = useRef();
  const itemDroppedEffect = useRef();
  const updateBalanceSheetEffect = useRef();
  const answerCorrectEffect = useRef();
  const answerWrongEffect = useRef();

  const navigate = useNavigate();

  const params = useParams();
  const levelNumber = Number(params.levelNumber);

  const { getCurrentBalancedEquationGameId } = useAuth();

  const gameId = getCurrentBalancedEquationGameId();

  const { soundStatus } = useSoundStatus();

  const [scenarioId, setScenarioId] = useState(0);

  const [questions, setQuestions] = useState([]);
  const [activeQuestionId, setActiveQuestionId] = useState(0);
  const [questionAnsweredCorrectly, setQuestionAnsweredCorrectly] = useState(0);

  const [isPlaying, setIsPlaying] = useState(false);

  const { data: games } = useQuery(gamesQuery()) || {};

  const [isModalVisible, setIsModalVisible] = useState(false);

  const [currentScenarioLevel, setCurrentScenarioLevel] = useState({});

  const [assets, setAssets] = useState(0);
  const [liabilities, setLiabilities] = useState(0);
  const [equity, setEquity] = useState(0);

  const [assetsChildren, setAssetsChildren] = useState([]);
  const [liabilitiesChildren, setLiabilitiesChildren] = useState([]);
  const [equityChildren, setEquityChildren] = useState([]);

  const assetsChildrenRef = useRef(assetsChildren);
  const liabilitiesChildrenRef = useRef(liabilitiesChildren);
  const equityChildrenRef = useRef(equityChildren);

  const [answerStatus, setAnswerStatus] = useState(null);
  const [isExplanationVisible, setIsExplanationVisible] = useState(false);
  const [explanationContent, setExplanationContent] = useState(null);

  const [isSubmitButtonShown, setIsSubmitButtonShown] = useState(true);

  const [filledRecords, setFilledRecords] = useState([]);

  const [optionsToBeFilled, setOptionsToBeFilled] = useState(0);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  
  const [isTopHintShown, setIsTopHintShown] = useState(false);
  const [isChangeHintShown, setIsChangeHintShown] = useState(false);
  const [changeHintContent, setChangeHintContent] = useState(null);
  const [isCoreHintShown, setIsCoreHintShown] = useState(false);
  const [coreHintContent, setCoreHintContent] = useState(null);
  const [isTransactionHintShown, setIsTransactionHintShown] = useState(false);
  const [transactionHintContent, setTransactionHintContent] = useState(null);
  const [isSubmitHintShown, setIsSubmitHintShown] = useState(false);
  const [wasSubmitHintShow, setWasSubmitHintShow] = useState(false);

  const [firstDropWasMade, setFirstDropWasMade] = useState(false);

  const [isItemsDisabled, setIsItemsDisabled] = useState(false);

  const [isSubmitHoverHintShown, setIsSubmitHoverHintShown] = useState(false);
  const [submitButtonHintContent, setSubmitButtonHintContent] = useState(<span></span>);

  const [resetKey, setResetKey] = useState(0);
  const [incorrectDecisions, setIncorrectDecisions] = useState([]);

  const [isSubmitButtonLoading, setIsSubmitButtonLoading] = useState(false);

  const [shouldBalanceSheetSlideIn, setShouldBalanceSheetSlideIn] = useState(levelNumber === 1 ? true : false);

  const { data: courses } = useQuery(coursesQuery());

  const scenarioLevelsResponse = useQuery(scenarioLevelsQuery({scenarioId: scenarioId, level: levelNumber})) || {};
  const scenarioLevels = scenarioLevelsResponse.data || null;

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsPlaying(true);
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (scenarioLevels?.length) {
      resetBalanceSheet();
      const scenarioLevel = scenarioLevels.find(scenarioLevel => scenarioLevel.level === levelNumber);

      setCurrentScenarioLevel(scenarioLevel);

      const { showChange, showCore, showTransaction } = getVisibleOptions(scenarioLevel?.name);

      const countOptionsToBeFilled = 
        !!(showChange && changeTypeOptions.length) + 
        !!(showCore && coreTypeOptions.length) + 
        !!(showTransaction && transactionTypeOptions.length);
      
      setOptionsToBeFilled(countOptionsToBeFilled + (showChange && changeTypeOptions.length ? 1 : 0));

      const unansweredQuestions = tutorialQuestions.map(question => ({
        id: question.id,
        value: question.value,
        description: question.description
      }));

      if(unansweredQuestions.length) {
        setQuestions(unansweredQuestions);
        setActiveQuestionId(unansweredQuestions[0].id);
      }
    }
  }, [scenarioLevels, levelNumber]);

  const shouldUpdateBalanceSheetSoundPlayRef = useRef(true);

  const getVisibleOptions = (scenarioName) => {
    switch (scenarioName) {
      case 'increase-decrease':
        return { showChange: true, showCore: false, showTransaction: false };
      case 'debit-credit':
        return { showChange: false, showCore: false, showTransaction: true };
      // debit-credit2 layers increase/decrease + debit/credit together
      // (design ['1','2']) — both option rows are surfaced in the
      // tutorial so the student practices the full chain.
      case 'debit-credit2':
        return { showChange: true, showCore: false, showTransaction: true };
      case 'accounts-impacted':
      case 'income-statement-impact':
        return { showChange: true, showCore: true, showTransaction: false };
      case 'all-types':
        return { showChange: true, showCore: true, showTransaction: true };
      default:
        return { showChange: true, showCore: false, showTransaction: false };
    }
  };
  
  const getHintContentDecisionTypes = () => {
    const name = currentScenarioLevel?.name;
    if (name === 'increase-decrease' || name === 'debit-credit') return 1;
    if (
      name === 'accounts-impacted'
      || name === 'income-statement-impact'
      || name === 'debit-credit2'
    ) return 2;
    return 3;
  }

  // Every BE level enforces the canonical 2-line entry — one record on
  // each side of the equation. Decision-type count affects how many
  // chips each record must carry, not the record count itself.
  const getRequiredRecordsForLevel = () => 2;

  const balanceSheetTotalChange = (optionName, value) => {
    if (soundStatus === 'enabled' && shouldUpdateBalanceSheetSoundPlayRef.current !== false) {
      shouldUpdateBalanceSheetSoundPlayRef.current = false;
      setTimeout(() => {
        updateBalanceSheetEffect.current.play().catch(e => console.log('Error playing item hit spike sound:', e));
        shouldUpdateBalanceSheetSoundPlayRef.current = true;
      }, 300);
    }

    if (optionName === 'Assets') {
      setAssets(prevAssets => prevAssets + value);
    } else if (optionName === 'Liabilities') {
      setLiabilities(prevLiabilities => prevLiabilities + value);
    } else if (optionName === 'Equity') {
      setEquity(prevEquity => prevEquity + value);
    }
  }
  
  const balanceSheetChildrenChange = (optionName, item, action, canRemove = false) => {
    if (optionName === 'Assets') {
      if (action === 'add') {
        const existingItemIndex = assetsChildrenRef.current.findIndex(child => child.name === item.name);
  
        if (existingItemIndex !== -1) {
          const updatedChildren = [...assetsChildrenRef.current];
          updatedChildren[existingItemIndex].value += item.value;
          if (updatedChildren[existingItemIndex].value === 0 && canRemove) {
            updatedChildren.splice(existingItemIndex, 1); // Remove the item if value is 0
          }
          setAssetsChildren(updatedChildren);
          assetsChildrenRef.current = updatedChildren;  // Update the ref
        } else {
          const newChildren = [...assetsChildrenRef.current, item];
          setAssetsChildren(newChildren);
          assetsChildrenRef.current = newChildren;  // Update the ref
        }
      } else if (action === 'remove') {
        const newChildren = assetsChildrenRef.current.filter(child => child.name !== item.name);
        setAssetsChildren(newChildren);
        assetsChildrenRef.current = newChildren;  // Update the ref
      }
    } else if (optionName === 'Liabilities') {
      if (action === 'add') {
        const existingItemIndex = liabilitiesChildrenRef.current.findIndex(child => child.name === item.name);
  
        if (existingItemIndex !== -1) {
          const updatedChildren = [...liabilitiesChildrenRef.current];
          updatedChildren[existingItemIndex].value += item.value;
          if (updatedChildren[existingItemIndex].value === 0 && canRemove) {
            updatedChildren.splice(existingItemIndex, 1); // Remove the item if value is 0
          }
          setLiabilitiesChildren(updatedChildren);
          liabilitiesChildrenRef.current = updatedChildren;  // Update the ref
        } else {
          const newChildren = [...liabilitiesChildrenRef.current, item];
          setLiabilitiesChildren(newChildren);
          liabilitiesChildrenRef.current = newChildren;  // Update the ref
        }
      } else if (action === 'remove') {
        const newChildren = liabilitiesChildrenRef.current.filter(child => child.name !== item.name);
        setLiabilitiesChildren(newChildren);
        liabilitiesChildrenRef.current = newChildren;  // Update the ref
      }
    } else if (optionName === 'Equity') {
      if (action === 'add') {
        const existingItemIndex = equityChildrenRef.current.findIndex(child => child.name === item.name);
  
        if (existingItemIndex !== -1) {
          const updatedChildren = [...equityChildrenRef.current];
          updatedChildren[existingItemIndex].value += item.value;
          if (updatedChildren[existingItemIndex].value === 0 && canRemove) {
            updatedChildren.splice(existingItemIndex, 1); // Remove the item if value is 0
          }
          setEquityChildren(updatedChildren);
          equityChildrenRef.current = updatedChildren;  // Update the ref
        } else {
          const newChildren = [...equityChildrenRef.current, item];
          setEquityChildren(newChildren);
          equityChildrenRef.current = newChildren;  // Update the ref
        }
      } else if (action === 'remove') {
        const newChildren = equityChildrenRef.current.filter(child => child.name !== item.name);
        setEquityChildren(newChildren);
        equityChildrenRef.current = newChildren;  // Update the ref
      }
    }
  };

  useEffect(() => {
    if (currentScenarioLevel?.name) {
      setIsTopHintShown(true);

      const isAccountsFirstScenario = currentScenarioLevel.name === 'accounts-impacted' || 
                                      currentScenarioLevel.name === 'income-statement-impact';
      const isDebitCreditScenario = currentScenarioLevel.name === 'debit-credit';

      // Change hint content
      let changeContent;
      if (currentScenarioLevel.name === 'increase-decrease') {
        changeContent = <span>Assuming the above transaction increase Assets, let's drag and drop the "+" sign to Assets record</span>;
      } else if (isAccountsFirstScenario) {
        changeContent = <span><b>Decision 2:</b> And should the account be increased or decreased.</span>;
      } else {
        changeContent = <span><b>Decision 1:</b> decide if the transaction increase and/or decrease the Balance Sheet Elements.</span>;
      }
      setChangeHintContent(changeContent);

      // Core hint content
      let coreContent;
      if (isAccountsFirstScenario) {
        coreContent = <span><b>Decision 1:</b> you're going to decide which 2 accounts are impacted.</span>;
      } else {
        coreContent = <span><b>Decision 2:</b> decide which accounts are impacted.</span>;
      }
      setCoreHintContent(coreContent);

      // Transaction hint content. debit-credit shows it as Decision 1
      // (sole decision); debit-credit2 layers it after the
      // increase/decrease decision (Decision 2); all-types has it
      // last after change + core (Decision 3).
      const isDebitCredit2Scenario = currentScenarioLevel.name === 'debit-credit2';
      let transactionContent;
      if (isDebitCreditScenario) {
        transactionContent = <span><b>Decision 1:</b> decide if the selected account should be debited or credited.</span>;
      } else if (isDebitCredit2Scenario) {
        transactionContent = <span><b>Decision 2:</b> decide if the selected account should be debited or credited.</span>;
      } else {
        transactionContent = <span><b>Decision 3:</b> decide if the selected account should be debited or credited.</span>;
      }
      setTransactionHintContent(transactionContent);
    }
  }, [currentScenarioLevel?.name]);

  useEffect(() => {
    const matchingRecords = filledRecords.filter(record => record.droppedItemsCount === optionsToBeFilled);
    const recordsWithDroppedItems = filledRecords.filter(record => record.droppedItemsCount > 0);
    const required = getRequiredRecordsForLevel();

    // Debits must balance credits by line count — 1D+1C for 2-record
    // levels, 2D+2C for 4-record levels. Implemented as: count each
    // distinct transactionId; expect exactly 2 distinct values with
    // equal counts. When the level doesn't surface transactionIds at
    // all (increase-decrease only), this is a no-op.
    const txCounts = {};
    matchingRecords.forEach(r => {
      if (r.transactionId) txCounts[r.transactionId] = (txCounts[r.transactionId] || 0) + 1;
    });
    const txValues = Object.values(txCounts);
    const isBalanced = txValues.length === 0 || (txValues.length === 2 && txValues[0] === txValues[1]);

    // Same-account duplicate rule (unchanged in spirit): two records
    // on the same account can't both carry the same change decision
    // or the same core (account) decision.
    let hasAccountConflict = false;
    for (let i = 0; i < matchingRecords.length && !hasAccountConflict; i++) {
      for (let j = i + 1; j < matchingRecords.length && !hasAccountConflict; j++) {
        const a = matchingRecords[i];
        const b = matchingRecords[j];
        if (a.optionAccountItemId === b.optionAccountItemId && (
          (a.changeId && b.changeId && a.changeId === b.changeId)
          || (a.coreId && b.coreId && a.coreId === b.coreId)
        )) {
          hasAccountConflict = true;
        }
      }
    }

    if (
      matchingRecords.length !== required
        || recordsWithDroppedItems.length !== required
        || !isBalanced
        || hasAccountConflict
    ) {
      setIsButtonDisabled(true);
    } else {
      setIsButtonDisabled(false);
      if(!wasSubmitHintShow) {
        const submitHintContent = currentScenarioLevel?.name === 'increase-decrease'
          ? <span>Great! The Equation now balances. Click submit.</span>
          : <span>Great! You can now submit because you have {required} records with {decisionTypesCount} {typesWord} of decisions in each.</span>;

        setSubmitButtonHintContent(submitHintContent);
        setIsSubmitHintShown(true);
        setWasSubmitHintShow(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filledRecords, optionsToBeFilled, wasSubmitHintShow, currentScenarioLevel?.name]);

  const transformedCoreTypeOptions = useMemo(() => {
    const businessType = courses?.[0]?.businesstype;
    const useOwnersCapital = businessType === 'Services' || businessType === 'Merchandising';
    
    if (!useOwnersCapital) {
      return coreTypeOptions; // Return original data as-is
    }
    
    return coreTypeOptions.map(option => {
      if (option.name === "Common Stock") {
        return {
          ...option,
          name: "Owner's Capital",
          description: "Owner's Capital, in accounting, refers to the total amount of investment made by the owner or owners in a business. It represents the initial contributions made by the owner(s) plus any additional investments over time."
        };
      }
      return option;
    });
  }, [courses]);

  const onDropAreaChange = ({
    id,
    droppedItemsCount,
    box,
    optionAccountItemId,
    optionName,
    changeId,
    changeAccountItemId,
    changeDescription,
    coreId,
    coreName,
    transactionId,
    transactionAccountItemId,
    transactionName
  }) => {
    if (soundStatus === 'enabled') {
      itemDroppedEffect.current.play().catch(e => console.log('Error playing item dropped sound:', e));
    }
    
    if(currentScenarioLevel?.name === 'increase-decrease' && !firstDropWasMade) {
      setIsChangeHintShown(true);
      setChangeHintContent(<span>Excellent! To balance the Accounting Equation, let’s assume that the transaction also increases Equity and drag and drop the “+” sign to Equity record</span>);
      setFirstDropWasMade(true);
    }
    if(shouldBalanceSheetSlideIn) {
      setShouldBalanceSheetSlideIn(false);
    }
    setFilledRecords(prevRecords => {
      const recordExists = prevRecords.some(record => record.id === id);
      let updatedRecords;
  
      if(recordExists) {
        updatedRecords = prevRecords.map(record => {
          if (record.id === id) {
            const updatedRecord = { ...record, droppedItemsCount };
            if(changeId) {
              updatedRecord.changeId = changeId;
              updatedRecord.changeAccountItemId = changeAccountItemId;
              updatedRecord.changeDescription = changeDescription;
            } else if(changeId === 0) {
              updatedRecord.changeId = null;
              updatedRecord.changeAccountItemId = null;
              updatedRecord.changeDescription = null;
            }
            if(coreId) {
              updatedRecord.coreId = coreId;
              updatedRecord.coreName = coreName;
            } else if(coreId === 0) {
              updatedRecord.coreId = null;
              updatedRecord.coreName = null;
            }
            if(transactionId){
              updatedRecord.transactionId = transactionId;
              updatedRecord.transactionAccountItemId = transactionAccountItemId;
              updatedRecord.transactionName = transactionName;
            } else if(transactionId === 0) {
              updatedRecord.transactionId = null;
              updatedRecord.transactionAccountItemId = null;
              updatedRecord.transactionName = null;
            }
            return updatedRecord;
          }
          return record;
        });
      } else {
        const recordItem = {
          id,
          droppedItemsCount,
          box,
          optionAccountItemId,
          optionName,
          changeId,
          changeAccountItemId,
          changeDescription,
          coreId,
          coreName,
          transactionId,
          transactionAccountItemId,
          transactionName
        };
        updatedRecords = [...prevRecords, recordItem];
      }
  
      return updatedRecords;
    });
  };

  const handleNextButtonClick = () => {
    setResetKey(prevKey => prevKey + 1);
    setFilledRecords([]);
    setIsSubmitButtonShown(true);
    setIsExplanationVisible(false);
    setIncorrectDecisions([]);
    resetBalanceSheet();

    if(questionAnsweredCorrectly) {
      setIsPlaying(false);
      setIsModalVisible(true);
    }

    setIsItemsDisabled(false);
  }

  const validateDecisionLocally = (record, activeQuestion) => {
    const correctDecisionsForLevel = tutorialCorrectDecisions[currentScenarioLevel?.name] || [];
    
    const results = [];

    if (record.changeId) {
      const matchingDecision = correctDecisionsForLevel.find(
        d => d.type === 'change' && 
            d.itemOption === record.optionAccountItemId && 
            d.selectedOption === record.changeId  // Changed from changeAccountItemId
      );
      
      results.push({
        id: record.changeId,
        decisionId: Date.now() + Math.random(),
        isCorrect: !!matchingDecision,
        type: 'change',
        description: record.changeDescription,
        optionAccountItemId: record.optionAccountItemId,
        optionName: record.optionName,
        isSecondDropArea: record.box === 'Bottom',
        questionId: activeQuestion.id,
        score: matchingDecision ? 10 : 0,
        name: record.changeDescription,
        value: activeQuestion.value
      });
    }

    if (record.coreId) {
      const matchingDecision = correctDecisionsForLevel.find(
        d => d.type === 'core' && 
            d.itemOption === record.optionAccountItemId && 
            d.selectedOption === record.coreId  // This was correct
      );
      
      results.push({
        id: record.coreId,
        decisionId: Date.now() + Math.random(),
        isCorrect: !!matchingDecision,
        type: 'core',
        optionAccountItemId: record.optionAccountItemId,
        optionName: record.optionName,
        isSecondDropArea: record.box === 'Bottom',
        questionId: activeQuestion.id,
        score: matchingDecision ? 10 : 0,
        name: record.coreName,
        value: activeQuestion.value
      });
    }

    if (record.transactionId) {
      const matchingDecision = correctDecisionsForLevel.find(
        d => d.type === 'transaction' && 
            d.itemOption === record.optionAccountItemId && 
            d.selectedOption === record.transactionId  // Changed from transactionAccountItemId
      );
      
      results.push({
        id: record.transactionId,
        decisionId: Date.now() + Math.random(),
        isCorrect: !!matchingDecision,
        type: 'transaction',
        optionAccountItemId: record.optionAccountItemId,
        optionName: record.optionName,
        isSecondDropArea: record.box === 'Bottom',
        questionId: activeQuestion.id,
        score: matchingDecision ? 10 : 0,
        name: record.transactionName,
        value: activeQuestion.value
      });
    }

    return results;
  };

  const handleSubmitButtonClick = async () => {
    setIsSubmitButtonLoading(true);
    shouldUpdateBalanceSheetSoundPlayRef.current = false;

    const latestFilledRecords = filledRecords.filter(filledRecord => filledRecord.droppedItemsCount);
    const activeQuestion = questions.find(question => question.id === activeQuestionId);
  
    const decisionResults = [];

    for (const record of latestFilledRecords) {
      const results = validateDecisionLocally(record, activeQuestion);
      decisionResults.push(...results);
    }

    setTimeout(() => {
      shouldUpdateBalanceSheetSoundPlayRef.current = true;
    }, 500);

    const correctResults = decisionResults.filter(decisionResult => decisionResult.isCorrect);

    const allResultsCorrect = correctResults.length === decisionResults.length;

    if (decisionResults.length && allResultsCorrect) {
      if (soundStatus === 'enabled') {
        answerCorrectEffect.current.play().catch(e => console.log('Error playing answer correct sound:', e));
      }

      setQuestionAnsweredCorrectly(decisionResults[0].questionId);
      setAnswerStatus('correct');

      // ... keep the existing explanation generation logic
      let firstUpdatedOptionName = '';
      switch(latestFilledRecords[0].optionName) {
        case 'Assets':
          firstUpdatedOptionName = 'an "Asset"';
          break;
        case 'Liabilities':
          firstUpdatedOptionName = 'a "Liability"';
          break;
        case 'Equity':
          firstUpdatedOptionName = 'an "Equity"';
          break;
        default:
          firstUpdatedOptionName = 'an "Asset"';
          break;
      }
      
      let secondUpdatedOptionName = '';
      switch(latestFilledRecords[1].optionName) {
        case 'Assets':
          secondUpdatedOptionName = 'an "Asset"';
          break;
        case 'Liabilities':
          secondUpdatedOptionName = 'a "Liability"';
          break;
        case 'Equity':
          secondUpdatedOptionName = 'an "Equity"';
          break;
        default:
          secondUpdatedOptionName = 'an "Asset"';
          break;
      }

      let explanation = '';
      switch(decisionResults.length) {
        case 2:
          explanation = `${firstUpdatedOptionName[0].toUpperCase() + firstUpdatedOptionName.slice(1)} should "${latestFilledRecords[0].changeDescription}" and ${secondUpdatedOptionName} should "${latestFilledRecords[1].changeDescription}". Click "Next" to continue.`;
          break;
        case 4: {
          // 4 decision results can come from two very different level
          // shapes:
          //   - accounts-impacted / income-statement-impact → change
          //     + core (account) chips per record → mention coreName
          //   - debit-credit2 → change + transaction (debit/credit)
          //     chips per record → no coreName, mention transactionName
          // Detect by which fields the records actually carry so the
          // template doesn't stringify a null.
          const isChangeTransactionCase = latestFilledRecords.every(
            (r) => r.transactionName && !r.coreName
          );
          if (isChangeTransactionCase) {
            explanation = `${firstUpdatedOptionName[0].toUpperCase() + firstUpdatedOptionName.slice(1)} should "${latestFilledRecords[0].changeDescription}" and be "${latestFilledRecords[0].transactionName.toLowerCase()}ed". ${secondUpdatedOptionName} should "${latestFilledRecords[1].changeDescription}" and be "${latestFilledRecords[1].transactionName.toLowerCase()}ed". Click "Next" to continue.`;
          } else {
            explanation = `${firstUpdatedOptionName[0].toUpperCase() + firstUpdatedOptionName.slice(1)} account, "${latestFilledRecords[0].coreName}", should "${latestFilledRecords[0].changeDescription}" and ${secondUpdatedOptionName} account, "${latestFilledRecords[1].coreName}", should "${latestFilledRecords[1].changeDescription}". Click "Next" to continue.`;
          }
          break;
        }
        case 6:
          explanation = `"${latestFilledRecords[0].coreName}" should be "${latestFilledRecords[0].transactionName}ed" since it's ${firstUpdatedOptionName} that "${latestFilledRecords[0].changeDescription}d". "${latestFilledRecords[1].coreName}" should be "${latestFilledRecords[1].transactionName}ed" since it's ${secondUpdatedOptionName} that "${latestFilledRecords[1].changeDescription}d". Click "Next" to continue.`;
          break;
        default:
          explanation = `${firstUpdatedOptionName[0].toUpperCase() + firstUpdatedOptionName.slice(1)} should "${latestFilledRecords[0].changeDescription}" and ${secondUpdatedOptionName} should "${latestFilledRecords[1].changeDescription}". Click "Next" to continue.`;
          break;
      }
      setExplanationContent(explanation);
      setIsExplanationVisible(true);
    } else {
      if (soundStatus === 'enabled') {
        answerWrongEffect.current.play().catch(e => console.log('Error playing answer wrong sound:', e));
      }

      const currentIncorrectDecisions = decisionResults.filter(decisionResult => !decisionResult.isCorrect);
      setIncorrectDecisions(currentIncorrectDecisions);

      // ... keep the existing balance sheet adjustment logic for wrong answers
      let coreAdjustmentValue;
      decisionResults.forEach(decision => {
        if (decision.type === 'change') {
          const lowerCaseOptionName = decision.optionName.toLowerCase();
          const adjustmentValue = decision.description === 'increase' ? -activeQuestion.value : activeQuestion.value;
          coreAdjustmentValue = adjustmentValue;
    
          switch (lowerCaseOptionName) {
            case 'assets':
              setAssets(prevAssets => prevAssets + adjustmentValue);
              break;
            case 'liabilities':
              setLiabilities(prevLiabilities => prevLiabilities + adjustmentValue);
              break;
            case 'equity':
              setEquity(prevEquity => prevEquity + adjustmentValue);
              break;
            default:
              console.error(`Unknown option name: ${lowerCaseOptionName}`);
          }
        } else if (decision.type === 'core') {
          balanceSheetChildrenChange(decision.optionName, {name: decision.name, value: coreAdjustmentValue}, 'add', true);
        }
      });

      setAnswerStatus('incorrect');
      setExplanationContent('Some or all of your decisions are not correct. Don\'t worry, you\'ll have another chance to answer this question. Click "Next" to continue.');
      setIsExplanationVisible(true);
    }

    setIsSubmitButtonShown(false);
    setIsSubmitButtonLoading(false);
    setIsItemsDisabled(true);

    setIsItemsDisabled(true);
  };

  const activeQuestion = questions.find(question => question.id === activeQuestionId);

  const handleReadyButtonClick = () => {
    navigate(`/balanced-equation/level/${levelNumber}`);
  }

  const decisionTypesCount = getHintContentDecisionTypes(levelNumber);
  const typesWord = decisionTypesCount > 1 ? 'types' : 'type';
  const topHintContent = currentScenarioLevel?.name === 'increase-decrease'
    ? <span>In this level, you are deciding how each transaction increase and/or decrease the Balance Sheet Elements (Assets, Liabilities, Equity).</span>
    : <span>In this level, you are making {decisionTypesCount} {typesWord} of decisions.</span>;

  const handleTopHintButtonClick = () => {
    setIsTopHintShown(false);
    
    if (isDebitCreditScenario) {
      setIsTransactionHintShown(true);  // Show transaction hint first
    } else if (isAccountsFirstScenario) {
      setIsCoreHintShown(true);  // Show core hint first
    } else {
      setIsChangeHintShown(true);
    }
  }

  const handleChangeHintButtonClick = () => {
    setIsChangeHintShown(false);

    // After the change (increase/decrease) hint, advance to whichever
    // decision comes next for this scenario:
    //   - showCore   → core hint (all-types, accounts-impacted variants)
    //   - showTransaction (no core) → transaction hint (debit-credit2)
    //   - neither → nothing else to show (increase-decrease)
    if (!isAccountsFirstScenario && !isDebitCreditScenario && showCore) {
      setIsCoreHintShown(true);
    } else if (showTransaction && !showCore) {
      setIsTransactionHintShown(true);
    }
  }

  const handleCoreHintButtonClick = () => {
    setIsCoreHintShown(false);

    if (isAccountsFirstScenario) {
      setIsChangeHintShown(true);  // Show change hint second
    } else if (showTransaction) {
      setIsTransactionHintShown(true);
    }
  }

  const handleTransactionHintButtonClick = () => {
    setIsTransactionHintShown(false);
  }

  const handleSubmitHintButtonClick = () => {
    setIsSubmitHintShown(false);
  }
  
  useEffect(() => {
    const playClickSound = () => {
      if (soundStatus === 'enabled') {
        clickEffect.current.play().catch(e => console.error('Error playing click sound:', e));
      }
    };

    document.addEventListener('click', playClickSound);

    return () => {
      document.removeEventListener('click', playClickSound);
    };
  }, [soundStatus]);

  /* Prevent sound from playing on other pages */
  useEffect(() => {
    const currentGameProcessEffect = gameProcessEffect.current;
  
    return () => {
      if (currentGameProcessEffect) {
        currentGameProcessEffect.pause();
      }
    };
  }, [window.location]);

  useEffect(() => {
    const clickAudio = new Audio(ClickSound);  
    clickAudio.preload = 'auto';  
    clickEffect.current = clickAudio;

    const itemDroppedAudio = new Audio(ItemDroppedSound);  
    itemDroppedAudio.preload = 'auto';  
    itemDroppedEffect.current = itemDroppedAudio;
    itemDroppedEffect.current.volume = 0.18;

    const updateBalanceSheetAudio = new Audio(UpdateBalanceSheetSound);  
    updateBalanceSheetAudio.preload = 'auto';  
    updateBalanceSheetEffect.current = updateBalanceSheetAudio;
    updateBalanceSheetEffect.current.volume = 0.3;
    
    const answerCorrectAudio = new Audio(AnswerCorrectSound);  
    answerCorrectAudio.preload = 'auto';  
    answerCorrectEffect.current = answerCorrectAudio;
    
    const answerWrongAudio = new Audio(AnswerWrongSound);  
    answerWrongAudio.preload = 'auto';  
    answerWrongEffect.current = answerWrongAudio;
  
    return () => {
      clickAudio.pause();
      itemDroppedAudio.pause();
      updateBalanceSheetAudio.pause();
      answerCorrectAudio.pause();
      answerWrongAudio.pause();
    };
  }, []);

  useEffect(() => {
    if(games) {
      const game = games.find(game => game.game_type.includes('Balanced Equation') && game.id === gameId);

      if(game && game.scenario) {
        setScenarioId(game.scenario);
      }
    }
  }, [games, gameId]);

  /* play background music */
  useEffect(() => {  
    if (isPlaying && soundStatus === 'enabled') {
      gameProcessEffect.current.volume = 0.09;
      gameProcessEffect.current.loop = true;
      gameProcessEffect.current.play().catch(e => console.log('Error playing game process sound:', e));
    } else {
      gameProcessEffect.current.pause();
    }
  
    return () => {
      gameProcessEffect.current.pause();
    };
  }, [isPlaying, soundStatus]);  

  const resetBalanceSheet = () => {
    setAssets(0);
    setLiabilities(0);
    setEquity(0);
    

    assetsChildrenRef.current = [];
    liabilitiesChildrenRef.current = [];
    equityChildrenRef.current = [];
    setAssetsChildren([]);
    setLiabilitiesChildren([]);
    setEquityChildren([]);
  }

  const handleSubmitButtonMouseEnter = () => {
    if (isSubmitButtonShown && isButtonDisabled) {
      let submitMessage = '';

      const recordsWithDroppedItems = filledRecords.filter(record => record.droppedItemsCount > 0);
      const required = getRequiredRecordsForLevel();

      if (recordsWithDroppedItems.length < required) {
        submitMessage = `To submit, there must be decisions in ${required} records.`;
      } else if (recordsWithDroppedItems.length === required) {
        if (showChange) {
          const allHaveChangeId = recordsWithDroppedItems.every(record => record.changeId);
          if (!allHaveChangeId) {
            submitMessage = `Increase or decrease needs to be in ${required} records.`;
          }
        }

        if (!submitMessage && showCore) {
          const allHaveCoreId = recordsWithDroppedItems.every(record => record.coreId);
          if (!allHaveCoreId) {
            submitMessage = `An account needs to be in ${required} records.`;
          }
        }

        if (!submitMessage && showTransaction) {
          const allHaveTransactionId = recordsWithDroppedItems.every(record => record.transactionId);
          if (!allHaveTransactionId) {
            submitMessage = `A debit or a credit needs to be in ${required} records.`;
          }
        }

        // Balance check for transactionId — debit count must equal
        // credit count. Then same-account duplicate check (pairwise)
        // for changeId / coreId.
        if (!submitMessage && showTransaction) {
          const txCounts = {};
          recordsWithDroppedItems.forEach(r => {
            if (r.transactionId) txCounts[r.transactionId] = (txCounts[r.transactionId] || 0) + 1;
          });
          const txValues = Object.values(txCounts);
          const balanced = txValues.length === 2 && txValues[0] === txValues[1];
          if (!balanced) {
            submitMessage = 'A debit or credit decision is missing.';
          }
        }
        if (!submitMessage) {
          outer:
          for (let i = 0; i < recordsWithDroppedItems.length; i++) {
            for (let j = i + 1; j < recordsWithDroppedItems.length; j++) {
              const a = recordsWithDroppedItems[i];
              const b = recordsWithDroppedItems[j];
              if (a.optionAccountItemId === b.optionAccountItemId) {
                if (showChange && a.changeId === b.changeId) {
                  submitMessage = 'An increase or decrease decision is missing.';
                  break outer;
                }
                if (showCore && a.coreId === b.coreId) {
                  submitMessage = 'An account decision is missing.';
                  break outer;
                }
              }
            }
          }
        }
      } else if (recordsWithDroppedItems.length > required) {
        submitMessage = `There are too many records; decisions should be in no more than ${required} records.`;
      }

      setSubmitButtonHintContent(submitMessage);
      setIsSubmitHoverHintShown(true);
    }
  };

  const handleSubmitButtonMouseLeave = () => {
    setIsSubmitHoverHintShown(false);
  }

  const { showChange, showCore, showTransaction } = getVisibleOptions(currentScenarioLevel?.name);

  const formattedChangeTypeOptions = changeTypeOptions.map(option => ({
    ...option,
    name: option.name === 'Increase' 
      ? <img src={SignPlus} alt="Plus" /> 
      : <img src={SignMinus} alt="Minus" />
  }));

  const isAccountsFirstScenario = currentScenarioLevel?.name === 'accounts-impacted' || currentScenarioLevel?.name === 'income-statement-impact';
  const isDebitCreditScenario = currentScenarioLevel?.name === 'debit-credit';

  const getTutorialRestrictions = () => {
    if (currentScenarioLevel?.name !== 'increase-decrease') {
      return null; // No restrictions for other scenarios
    }
    
    if (!firstDropWasMade) {
      // Step 1: Only "+" to Assets
      return {
        allowedOptionName: 'Assets',
        allowedItemDescription: 'Increase'
      };
    } else {
      // Step 2: Only "+" to Equity
      return {
        allowedOptionName: 'Equity',
        allowedItemDescription: 'Increase'
      };
    }
  };

  const tutorialRestrictions = getTutorialRestrictions();

  return(
    <Layout
      backgroundColor="rgba(0, 27, 59, 0.04)"
      isHeaderVisible={true}
      isFooterVisible={true}
      totalScore={0}
      scoreLabel="Points"
      isExplanationVisible={isExplanationVisible}
      explanationName="Bailey"
      isAvatarVisible={false}
      answerStatus={answerStatus}
      explanationContent={explanationContent}
    >
      {
        (isTopHintShown || isChangeHintShown || isCoreHintShown || isTransactionHintShown || isSubmitHintShown) &&
          <div className='background-overlay'/>
      }
      <TransactionRecords 
        transactionText={activeQuestion?.description}
        value={activeQuestion?.value}
        topOptions={topOptions}
        changeTypeOptions={showChange ? formattedChangeTypeOptions : []}
        coreTypeOptions={showCore ? transformedCoreTypeOptions : []}
        transactionTypeOptions={showTransaction ? transactionTypeOptions : []}
        onChangeItemDrop={balanceSheetTotalChange}
        onCoreItemDrop={balanceSheetChildrenChange}
        onDropAreaChange={onDropAreaChange}
        isSubmitButtonShown={isSubmitButtonShown}
        onSubmitButtonClick={handleSubmitButtonClick}
        onSubmitButtonMouseEnter={handleSubmitButtonMouseEnter}
        onSubmitButtonMouseLeave={handleSubmitButtonMouseLeave}
        onNextButtonClick={handleNextButtonClick}
        isSubmitButtonLoading={isSubmitButtonLoading}
        isButtonDisabled={isButtonDisabled}
        resetKey={resetKey}
        incorrectDecisions={incorrectDecisions}
        isTopHintShown={isTopHintShown}
        topHintContent={topHintContent}
        onTopHintButtonClick={handleTopHintButtonClick}
        isChangeHintShown={isChangeHintShown}
        isCoreHintShown={isCoreHintShown}
        isTransactionHintShown={isTransactionHintShown}
        changeHintContent={changeHintContent}
        onChangeHintButtonClick={handleChangeHintButtonClick}
        coreHintContent={coreHintContent}
        onCoreHintButtonClick={handleCoreHintButtonClick}
        transactionHintContent={transactionHintContent}
        onTransactionHintButtonClick={handleTransactionHintButtonClick}
        isSubmitHintShown={isSubmitHintShown || isSubmitHoverHintShown}
        submitHintContent={submitButtonHintContent}
        onSubmitHintButtonClick={!isSubmitHoverHintShown ? handleSubmitHintButtonClick : undefined}
        isItemsDisabled={isItemsDisabled}
        level={levelNumber}
        selectedDesignTypes={currentScenarioLevel?.design}
        equation={currentScenarioLevel?.equation}
        optionsToBeFilled={optionsToBeFilled}
        tutorialRestrictions={tutorialRestrictions}
      />
        <BalanceSheet
          assets={assets}
          liabilities={liabilities}
          equity={equity}
          assetsChildren={assetsChildren}
          liabilitiesChildren={liabilitiesChildren}
          equityChildren={equityChildren}
          shouldSlideIn={shouldBalanceSheetSlideIn}
        />
      {isModalVisible && (
        <Modal
          content={ 
            <>
              <div className="modal-content align-center">
                <p>Awesome!</p>
                <p>You’ve completed the tutorial!</p>
                <p>Ready to play? </p>
              </div>
              <div className="next-button" onClick={handleReadyButtonClick}>I’m Ready!</div>
            </>
          }
        />
      )}
    </Layout>
  );
}
